import clsx from 'clsx'
import type { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { TouchableWithoutFeedback, View } from 'react-native'

import { ActionSheet } from '@/src/elements/action/ActionSheet'
import { Button } from '@/src/elements/button/default'
import type { TListboxItemProps } from '@/src/elements/listbox/useListboxItem'
import { Icon } from '@/src/elements/media/Icon'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'
import { isMobile } from '@/src/general/utils/isMobile'

import type { TChatMessageProps } from '../ChatMessage.types'
import { ChatMessageWarning } from './ChatMessageWarning'

type TChatMessageTextWrapperProps = Pick<
  TChatMessageProps,
  'createdAt' | 'isSender' | 'lastOfBatch' | 'onDeleteMessage' | 'onReportMessage' | 'showWarning'
> & {
  children: ({ mainLastOfBatch }: { mainLastOfBatch: boolean }) => ReactNode
}

export const ChatMessageTextWrapper = ({
  children,
  createdAt,
  isSender,
  lastOfBatch,
  onDeleteMessage,
  onReportMessage,
  showWarning,
}: TChatMessageTextWrapperProps) => {
  const items: TListboxItemProps[] = isSender
    ? [
        {
          children: <FormattedMessage id="message.delete" />,
          startContent: <Icon name={ICON.TRASH} />,
          onPress: onDeleteMessage,
        },
      ]
    : [
        {
          children: <FormattedMessage id="app.report-abuse.report" />,
          startContent: <Icon name={ICON.ALERT_TRIANGLE} />,
          onPress: onReportMessage,
        },
      ]

  const childrenWithLastOfBatch = children({ mainLastOfBatch: !showWarning && lastOfBatch })

  const warning = showWarning && (
    <ChatMessageWarning createdAt={createdAt} isSender={isSender} lastOfBatch={lastOfBatch} />
  )

  if (isMobile()) {
    return (
      <>
        <ActionSheet items={items}>
          {({ onPress }) => (
            <TouchableWithoutFeedback onLongPress={onPress}>{childrenWithLastOfBatch}</TouchableWithoutFeedback>
          )}
        </ActionSheet>
        {warning}
      </>
    )
  }

  return (
    <>
      <View className={clsx('w-full gap-1', isSender ? 'flex-row-reverse' : 'flex-row')}>
        {childrenWithLastOfBatch}
        <ActionSheet items={items}>
          <Button
            testID={TEST_ID.CONVERSATION.MESSAGE.OPTION_BUTTON}
            isIconOnly
            startIcon={ICON.ELLIPSIS_VERTICAL}
            contrast="low"
            colors="default"
            variant="light"
            size="sm"
            className="self-start"
          />
        </ActionSheet>
      </View>
      {warning}
    </>
  )
}
